/**
 * useForceRefresh Hook
 * 
 * Subscribes to Supabase broadcast channel for force refresh events.
 * Light QA Contract Compliant - Flag-gated, no impact when OFF.
 * 
 * @owner system-admin
 * @created 2025-12-09
 * @expiryDate 2026-03-09
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { globalFeatureFlags } from '@/lib/featureFlags';

export interface ForceRefreshEvent {
  message: string;
  triggeredAt: string;
  triggeredBy: string | null;
  countdownSeconds?: number;
}

interface UseForceRefreshReturn {
  refreshPending: boolean;
  refreshEvent: ForceRefreshEvent | null;
  triggerRefresh: (message: string) => Promise<boolean>;
  dismissRefresh: () => void;
}

const CHANNEL_NAME = 'app-updates';
const EVENT_NAME = 'force-refresh';
const DEFAULT_COUNTDOWN = 10;

/**
 * Structured logging for force refresh events
 */
function logForceRefresh(action: string, data?: Record<string, unknown>) {
  const logEntry = {
    timestamp: new Date().toISOString(),
    component: 'useForceRefresh',
    action,
    ...data,
  };
  
  if (import.meta.env.DEV) {
    console.log('[ForceRefresh]', logEntry);
  }
}

export function useForceRefresh(): UseForceRefreshReturn {
  const [refreshPending, setRefreshPending] = useState(false);
  const [refreshEvent, setRefreshEvent] = useState<ForceRefreshEvent | null>(null);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  
  // Check if feature flag is enabled
  const flagEnabled = globalFeatureFlags.isEnabled('ENABLE_FORCE_REFRESH');
  
  useEffect(() => {
    // If flag is OFF, do nothing (no subscription, no behavior change)
    if (!flagEnabled) {
      logForceRefresh('flag_disabled', { flag: 'ENABLE_FORCE_REFRESH' });
      return;
    }
    
    logForceRefresh('subscribing', { channel: CHANNEL_NAME });
    
    // Subscribe to broadcast channel
    const channel = supabase
      .channel(CHANNEL_NAME)
      .on('broadcast', { event: EVENT_NAME }, (payload) => {
        logForceRefresh('event_received', { payload: payload.payload });
        
        const event: ForceRefreshEvent = {
          message: payload.payload?.message || 'Ứng dụng cần được cập nhật',
          triggeredAt: payload.payload?.triggeredAt || new Date().toISOString(),
          triggeredBy: payload.payload?.triggeredBy || null,
          countdownSeconds: payload.payload?.countdownSeconds || DEFAULT_COUNTDOWN,
        };
        
        setRefreshEvent(event);
        setRefreshPending(true);
      })
      .subscribe((status) => {
        logForceRefresh('subscription_status', { status });
      });
    
    channelRef.current = channel;
    
    return () => {
      logForceRefresh('unsubscribing', { channel: CHANNEL_NAME });
      supabase.removeChannel(channel);
      channelRef.current = null;
    };
  }, [flagEnabled]);
  
  /**
   * Trigger force refresh for all connected clients
   * Admin only - used by ForceRefreshControl component
   */
  const triggerRefresh = useCallback(async (message: string): Promise<boolean> => {
    if (!flagEnabled) {
      logForceRefresh('trigger_blocked', { reason: 'flag_disabled' });
      return false;
    }
    
    try {
      logForceRefresh('trigger_start', { message });
      
      // Get current user for audit
      const { data: { user } } = await supabase.auth.getUser();
      
      const payload: ForceRefreshEvent = {
        message: message || 'Ứng dụng cần được cập nhật phiên bản mới',
        triggeredAt: new Date().toISOString(),
        triggeredBy: user?.email || user?.id || null,
        countdownSeconds: DEFAULT_COUNTDOWN,
      };
      
      // Broadcast to all connected clients
      const channel = supabase.channel(CHANNEL_NAME);
      await channel.send({
        type: 'broadcast',
        event: EVENT_NAME,
        payload,
      });
      
      // Update app_config for persistence (audit log)
      await supabase
        .from('app_config')
        .update({
          value: {
            enabled: true,
            message: payload.message,
            triggered_at: payload.triggeredAt,
            triggered_by: payload.triggeredBy,
          },
          updated_at: new Date().toISOString(),
          updated_by: user?.id,
        })
        .eq('key', 'force_refresh');
      
      logForceRefresh('trigger_success', { payload });
      return true;
      
    } catch (error) {
      logForceRefresh('trigger_error', { error: String(error) });
      return false;
    }
  }, [flagEnabled]);
  
  /**
   * Dismiss refresh notification (user chose not to refresh)
   */
  const dismissRefresh = useCallback(() => {
    logForceRefresh('dismissed', { event: refreshEvent });
    setRefreshPending(false);
    setRefreshEvent(null);
  }, [refreshEvent]);
  
  return {
    refreshPending,
    refreshEvent,
    triggerRefresh,
    dismissRefresh,
  };
}
